import { FunctionComponent, useCallback, Fragment, useMemo } from 'react'
import { Avatar } from '@conexasaude/hero'
import navbarStyles from '@/components/Navbar/navbar-style'
import logo from '@/assets/logo-conexa.svg'
import Icon from '@/components/HeroIcons'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { useCheckoutContext } from '@/modules/Checkout/contexts/CheckoutContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { Container } from '@conexasaude/hero-web'
import { MenuItem } from './types'
import { RenderIf } from '../RenderIf'
import { useQueryClient } from '@tanstack/react-query'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'

const Navbar: FunctionComponent = () => {
  const navStyles = navbarStyles()
  const { user, clinic, signOut, users } = useAuthContext()
  const { pathname } = useLocation()
  const history = useHistory()
  const { logoutFromFirebase, logAnalyticsEvent } = useFirebaseContext()
  const { clearRealtimeAttendance } = useRealtimeAttendanceContext()
  const { setCard } = useCheckoutContext()
  const { embed } = useThemeGeneratorContext()
  const { showModal } = useModalContext()
  const queryClient = useQueryClient()

  const handleAnalytics = useCallback(
    (eventName: string) => {
      if (!eventName) return
      logAnalyticsEvent(eventName)
    },
    [logAnalyticsEvent],
  )

  const logout = useCallback(
    (general: boolean) => {
      signOut(general)
      logoutFromFirebase()
      clearRealtimeAttendance()
      setCard(null)
      queryClient.removeQueries()

      // obrigatório para atualizar chave do zendesk
      if (general) {
        window.location.href = '/login'
      }
    },
    [
      clearRealtimeAttendance,
      logoutFromFirebase,
      signOut,
      setCard,
      queryClient,
    ],
  )

  const handleChangeProfile = useCallback(() => {
    const isImmediateWaitingRoom = pathname.includes(
      '/atendimento-imediato/waiting',
    )

    const [, firstPathname, secondPathname] = pathname.split('/')
    const isScheduledWaitingRoom =
      firstPathname === 'atendimento-agendado' && /^\d+$/.test(secondPathname)

    const isVideoRoom = pathname.includes('/chamada')

    if (isImmediateWaitingRoom || isScheduledWaitingRoom || isVideoRoom) {
      return
    }
    // handleAnalytics('BTN_HomeCNX_Menu_TrocarPerfil')
    logout(false)
    history.push('/escolha')
  }, [history, logout, pathname])

  const handleLogout = useCallback(() => {
    const isImmediateWaitingRoom = pathname.includes(
      '/atendimento-imediato/waiting',
    )

    const [, firstPathname, secondPathname] = pathname.split('/')
    const isScheduledWaitingRoom =
      firstPathname === 'atendimento-agendado' && /^\d+$/.test(secondPathname)

    if (isImmediateWaitingRoom || isScheduledWaitingRoom) {
      showModal('LEAVE_WAITING_ROOM', {
        title: 'Deseja sair da sala de espera?',
      })

      return
    }

    logout(true)
  }, [pathname, logout, showModal])

  const handleMyProfile = useCallback(() => {
    // handleAnalytics('BTN_HomeCNX_Menu_MeuPerfil')
    history.push('/perfil')
  }, [history])

  // const handlePassword = useCallback(() => {
  //   handleAnalytics('')
  //   history.push('/perfil')
  // }, [handleAnalytics, history])

  const handleMyCards = useCallback(() => {
    // handleAnalytics('BTN_HomeCNX_Menu_MeusCartoes')
    history.push('/pagamentos')
  }, [history])

  const handleSupport = useCallback(() => {
    // handleAnalytics('BTN_HomeCNX_Menu_Duvidas')
    history.push('/duvidas')
  }, [history])

  const showConsultations = useMemo(() => {
    if (
      !clinic?.flag?.HabilitarAgendamentoOperadora &&
      !clinic?.flag?.PossuiAlgumServicoHabilitado &&
      clinic?.flag?.CnxPlus
    ) {
      return false
    }

    if (clinic?.flag?.HabilitarAgendamentoOperadora && clinic?.flag?.CnxPlus) {
      return true
    }

    if (clinic?.flag?.CnxPlus && !clinic?.flag?.PossuiAlgumServicoHabilitado) {
      return false
    }

    return true
  }, [
    clinic?.flag?.CnxPlus,
    clinic?.flag?.HabilitarAgendamentoOperadora,
    clinic?.flag?.PossuiAlgumServicoHabilitado,
  ])

  const menuItems: MenuItem[] = [
    {
      id: 1,
      label: 'Início',
      type: 'link',
      path: '/',
      class: pathname === '/' ? 'active' : '',
      icon: 'home',
      show: true,
      analyticsEvent: 'Pac_Home_HeaderInicio',
    },
    {
      id: 2,
      label: 'Consultas',
      type: 'link',
      path: '/meus-atendimentos',
      class: pathname === '/meus-atendimentos' ? 'active' : '',
      icon: 'medicine-folder',
      show: showConsultations,
      analyticsEvent: clinic?.flag?.ClinicaUnificadaPV
        ? 'Pac_Agendamento_MenuConsultas'
        : 'Pac_Home_HeaderConsultas',
    },
    {
      id: 3,
      type: 'link',
      path: '/conexa-plus',
      label: 'Conexa Plus',
      class: pathname.includes('/conexa-plus') ? 'active' : '',
      icon: 'medical-cross',
      show: clinic?.flag?.CnxPlus,
      analyticsEvent: 'Pac_Home_HeaderCNXPlus',
    },

    {
      id: 4,
      type: 'button',
      label: 'Suporte',
      onClick: openWhatsappSupport,
      class: 'support-item-menu',
      icon: 'headphone',
      show: true,
      analyticsEvent: 'Pac_Home_HeaderSuporte',
    },
    {
      id: 5,
      type: 'link',
      label: 'Meus Benefícios',
      path: '/meus-beneficios',
      class: `${user?.isAcceptedBenefitTerm ? '' : 'notification'} ${
        pathname === '/meus-beneficios' ? 'active' : ''
      }`,
      icon: 'benefits-icon-menu',
      show: clinic?.flag?.SmallBusiness,
    },
  ]
  if (!embed.value) {
    return (
      <Fragment>
        <Container>
          <nav className="navContainer" css={navStyles.navContainer}>
            <img
              src={clinic?.logos.show ? clinic.logos.menu : logo}
              alt="Logo"
              className={clinic?.logos.show ? '' : 'conexa-logo'}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = logo
              }}
              css={navStyles.navLogo}
            />
            <ul css={navStyles.picNotification}>
              <li css={navStyles.navProfile}>
                <Avatar label={user?.shortName} size={'md'} src={user?.photo} />
                <Icon
                  name="arrow-down"
                  themeColor={['extra', 'gray_icon']}
                  size={'10px'}
                  style={{ marginLeft: '15px' }}
                />
                <div
                  className={
                    users?.length > 1
                      ? 'suspense-nav'
                      : 'suspense-nav suspense-nav--less'
                  }
                >
                  <ul className="suspense-nav__container">
                    <li onClick={handleMyProfile}>Meu Perfil</li>
                    <RenderIf condition={clinic?.flag?.PermiteLoginPaciente}>
                      <li onClick={() => history.push('/confirmar-senha')}>
                        Senha
                      </li>
                    </RenderIf>
                    <RenderIf condition={clinic?.canPrivateAttendance}>
                      <li onClick={handleMyCards}>Meus Cartões</li>
                    </RenderIf>
                    <RenderIf condition={users?.length > 1}>
                      <li onClick={handleChangeProfile}>Trocar de Perfil</li>
                    </RenderIf>
                    <li onClick={handleSupport}>Dúvidas</li>
                    <li onClick={handleLogout}>Sair</li>
                  </ul>
                </div>
              </li>
            </ul>
          </nav>
        </Container>
        <nav css={navStyles.navContainerBreadcrumb}>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <ul>
                  {menuItems.map(item => {
                    return (
                      item.show && (
                        <li
                          onClick={() => handleAnalytics(item.analyticsEvent)}
                          key={item.id}
                          className={item.class}
                        >
                          <Icon
                            name={item.icon}
                            themeColor={['common', 'white']}
                          />
                          {item.type === 'button' ? (
                            <button
                              className={item.class}
                              onClick={item.onClick}
                            >
                              {item.label}
                            </button>
                          ) : (
                            <Link to={item.path}>{item.label}</Link>
                          )}
                        </li>
                      )
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </Fragment>
    )
  } else return <div className="mb-3" />
}

export default Navbar
